// Tax returns service. Fase B: datos reales vía Supabase (RLS por tenant);
// sin variables de entorno usa fixtures (demo/CI).
import type { TaxReturnStatus } from '@/types/domain';
import { supabase, hasBackend } from '@/lib/supabase';
import { toPrefixTsQuery } from '@/lib/search';
import { loadFailed } from './loadError';
import { fmtDate } from '@/lib/format';

export interface ReturnSummary {
  id: string;
  client: string;
  year: number;
  season: string;
  status: TaxReturnStatus;
  preparer: string;
  dueDate: string;
  updatedAt: string;
}

export interface ReturnsFilter {
  query?: string;
  status?: TaxReturnStatus | 'all';
  /** Paginación (VLT-312): tamaño de página y desplazamiento. */
  limit?: number;
  offset?: number;
}

/** Tamaño de página de las listas (VLT-312). */
export const RETURNS_PAGE = 50;

/** Matriz de transiciones (VLT-109) — ESPEJO de la migración 0039: la UI solo
 *  ofrece lo alcanzable y la base lo hace cumplir de todas formas. Regresiones
 *  explícitas de un paso; lo firmado no se «desfirma»; delivered es terminal. */
export const RETURN_TRANSITIONS: Record<TaxReturnStatus, TaxReturnStatus[]> = {
  pending: ['received'],
  received: ['in_review', 'pending'],
  in_review: ['completed', 'received'],
  completed: ['signed', 'in_review'],
  signed: ['delivered'],
  delivered: [],
};

export type AdvanceResult = 'ok' | 'transicion_invalida' | 'error';

export interface ReturnsService {
  list(filter?: ReturnsFilter): Promise<ReturnSummary[]>;
  /** counts per status for the pipeline summary */
  pipeline(): Promise<Record<TaxReturnStatus, number>>;
  /** Búsqueda para ⌘K (VLT-309): server-side sobre search_vector + nombre del cliente. */
  search(query: string): Promise<ReturnSummary[]>;
  /** Estado actual de UNA declaración (para el control del workspace). */
  status(id: string): Promise<TaxReturnStatus | null>;
  /** Avanza (o regresa un paso) el estado — la base valida la matriz 0039. */
  advance(id: string, to: TaxReturnStatus): Promise<AdvanceResult>;
}

const CLIENTS = ['María Santos', 'Andrés Rivera', 'Lucía Gómez', 'Núñez LLC', 'Kwame Osei', 'Diego Fuentes', 'Innova LLC', 'Rosa Peralta'];
const PREPARERS = ['G. Cassimmit', 'P. Herrera', 'S. Batista'];
const STATUS_CYCLE: TaxReturnStatus[] = ['pending', 'received', 'in_review', 'in_review', 'completed', 'signed', 'delivered', 'pending'];

const FIXTURES: ReturnSummary[] = Array.from({ length: 18 }, (_, i) => ({
  id: `tr${i + 1}`,
  client: CLIENTS[(i * 5) % CLIENTS.length],
  year: i < 14 ? 2025 : 2024,
  season: i < 14 ? 'Spring 2026' : 'Spring 2025',
  status: STATUS_CYCLE[i % STATUS_CYCLE.length],
  preparer: PREPARERS[i % PREPARERS.length],
  dueDate: i % 4 === 0 ? '2026-07-20' : '2026-08-15',
  updatedAt: `hace ${(i % 6) + 1} h`,
}));

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface ReturnRow {
  id: string;
  tax_year: number;
  season: string | null;
  status: TaxReturnStatus;
  updated_at: string;
  clients: { first_name: string; last_name: string } | null;
  preparer: { full_name: string | null } | null;
}

const rowToSummary = (r: ReturnRow): ReturnSummary => ({
  id: r.id,
  client: r.clients ? `${r.clients.first_name} ${r.clients.last_name}`.trim() : '—',
  year: r.tax_year,
  season: r.season ?? '',
  status: r.status,
  preparer: r.preparer?.full_name ?? '—',
  // La fecha límite llega con el módulo de plazos (Fase 4); '—' hasta entonces.
  dueDate: '—',
  updatedAt: fmtDate(r.updated_at),
});

const SEL = 'id, tax_year, season, status, updated_at, clients(first_name, last_name), preparer:profiles!preparer_id(full_name)';
const SEL_BY_CLIENT = 'id, tax_year, season, status, updated_at, clients!inner(first_name, last_name), preparer:profiles!preparer_id(full_name)';

/** Filtrado 100% en Postgres (VLT-312): estado por eq, texto por doble camino
 *  (search_vector con prefijos + nombre del cliente vía embed !inner), con
 *  rango de paginación. El navegador ya no filtra ni trae de más. */
const fetchFiltered = async ({ query = '', status = 'all', limit = RETURNS_PAGE, offset = 0 }: ReturnsFilter): Promise<ReturnSummary[]> => {
  const q = query.trim();
  const base = (select: string) => {
    let req = supabase.from('tax_returns').select(select).is('deleted_at', null);
    if (status !== 'all') req = req.eq('status', status);
    return req.order('updated_at', { ascending: false }).range(offset, offset + limit - 1);
  };
  if (!q) {
    const { data, error } = await base(SEL);
    if (error) loadFailed('returns.list', error);
    return (data as unknown as ReturnRow[]).map(rowToSummary);
  }
  const tsq = toPrefixTsQuery(q);
  const [byVector, byClient] = await Promise.all([
    tsq ? base(SEL).textSearch('search_vector', tsq, { config: 'simple' }) : Promise.resolve({ data: [], error: null }),
    base(SEL_BY_CLIENT).or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%`, { referencedTable: 'clients' }),
  ]);
  if (byVector.error) console.warn('[VaulTex] returns.list:', byVector.error.message);
  if (byClient.error) console.warn('[VaulTex] returns.list(client):', byClient.error.message);
  // Las dos vías caídas = no sabemos (error), no «sin resultados».
  if ((byVector.error || !tsq) && byClient.error) loadFailed('returns.search', byClient.error);
  const rows = [...((byVector.data ?? []) as unknown as ReturnRow[]), ...((byClient.data ?? []) as unknown as ReturnRow[])];
  const seen = new Set<string>();
  return rows.filter((r) => !seen.has(r.id) && seen.add(r.id)).slice(0, limit).map(rowToSummary);
};

const realReturns: ReturnsService = {
  list: (filter = {}) => fetchFiltered(filter),

  async pipeline() {
    // Conteos por estado sin traer filas: un head-count por estado en paralelo.
    const states: TaxReturnStatus[] = ['pending', 'received', 'in_review', 'completed', 'signed', 'delivered'];
    const counts = await Promise.all(
      states.map((s) =>
        supabase.from('tax_returns').select('id', { count: 'exact', head: true }).is('deleted_at', null).eq('status', s),
      ),
    );
    const acc = {} as Record<TaxReturnStatus, number>;
    const failed = counts.find((c) => c.error);
    // Un conteo caído NO es 0: el llamador muestra «—».
    if (failed?.error) loadFailed('returns.pipeline', failed.error);
    states.forEach((s, i) => {
      acc[s] = counts[i].count ?? 0;
    });
    return acc;
  },

  search: (query) => (query.trim() ? fetchFiltered({ query, limit: 8 }) : Promise.resolve([])),

  async status(id) {
    const { data, error } = await supabase
      .from('tax_returns').select('status').eq('id', id).maybeSingle();
    if (error) loadFailed('returns.status', error);
    if (!data) return null;
    return (data as { status: TaxReturnStatus }).status;
  },

  async advance(id, to) {
    const { error } = await supabase
      .from('tax_returns')
      .update({ status: to } as never)
      .eq('id', id);
    if (!error) return 'ok';
    if (error.message.includes('transicion_invalida')) return 'transicion_invalida';
    console.warn('[VaulTex] returns.advance:', error.message);
    return 'error';
  },
};

// ─── Implementación demo (fixtures, Fase A) ──────────────────────────────────

const fixtureReturns: ReturnsService = {
  list: ({ query = '', status = 'all', limit = RETURNS_PAGE, offset = 0 } = {}) => {
    const q = query.trim().toLowerCase();
    return Promise.resolve(
      FIXTURES.filter(
        (r) =>
          (status === 'all' || r.status === status) &&
          (!q || `${r.client} ${r.preparer} ${r.year}`.toLowerCase().includes(q)),
      ).slice(offset, offset + limit),
    );
  },
  pipeline: () => {
    const acc = { pending: 0, received: 0, in_review: 0, completed: 0, signed: 0, delivered: 0 } as Record<TaxReturnStatus, number>;
    FIXTURES.forEach((r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
    });
    return Promise.resolve(acc);
  },
  search: (query) => {
    const q = query.trim().toLowerCase();
    if (!q) return Promise.resolve([]);
    return Promise.resolve(
      FIXTURES.filter((r) => `${r.client} ${r.year} ${r.status} ${r.season}`.toLowerCase().includes(q)).slice(0, 6),
    );
  },
  status: (id) => Promise.resolve(FIXTURES.find((r) => r.id === id)?.status ?? null),
  advance: (id, to) => {
    const r = FIXTURES.find((x) => x.id === id);
    if (!r) return Promise.resolve('error' as const);
    if (!RETURN_TRANSITIONS[r.status].includes(to)) return Promise.resolve('transicion_invalida' as const);
    r.status = to;
    return Promise.resolve('ok' as const);
  },
};

export const returnsService: ReturnsService = hasBackend ? realReturns : fixtureReturns;
